'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import { developmentDiagnosticsEnabled } from '../../../lib/diagnostics';
import { getDataTestId } from '../../../lib/props';
import { createRenderIdentityPlan } from '../../../lib/render-identities';
import { cn } from '../../../lib/utils';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../../ui/table';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraIconButton } from '../ensigns/icon-button';
import { SpecraEmptyState } from '../lieutenants/empty-state';
import { SpecraInlineBanner } from '../lieutenants/inline-banner';
import { SpecraPagination } from '../lieutenants/pagination';
/**
 * Every id in a tree that has children, e.g. for an "Expand all" control:
 * `setExpandedIds(specraExpandableRowIds(rows, getRowId, getRowChildren))`.
 */ export function specraExpandableRowIds(rows, getRowId, getRowChildren) {
    const ids = [];
    const walk = (list)=>{
        for (const row of list){
            const children = getRowChildren(row);
            if (children?.length) {
                ids.push(getRowId(row));
                walk(children);
            }
        }
    };
    walk(rows);
    return ids;
}
const rowToneClass = {
    neutral: 'bg-muted/50',
    success: 'bg-success-muted/60 hover:bg-success-muted',
    warning: 'bg-warning-muted/60 hover:bg-warning-muted',
    danger: 'bg-danger-muted/60 hover:bg-danger-muted',
    info: 'bg-info-muted/60 hover:bg-info-muted'
};
function isInteractiveTarget(target) {
    return target instanceof Element && Boolean(target.closest('a, button, input, select, textarea, summary, label, audio[controls], video[controls], [contenteditable="true"], [role="button"], [role="link"], [role="checkbox"], [role="menuitem"], [role="option"], [role="switch"]'));
}
/** Domain-neutral operational table used throughout the Delivery screens. */ function SpecraDataTableInner({ 'aria-busy': ariaBusy, actionsLabel, caption, className, columns, data, defaultExpandedIds, empty, emptyAction, emptyDescription, emptyTitle = 'No records', error, expandedIds, expandToggleTestId, filterRow, footerRow, framed = false, getExpandedRowProps, getExpandToggleLabel, getExpandToggleProps, getRowChildren, getRowId, getRowActionLabel, getRowLabel, getRowProps, getRowTone, groups, isRowExpandable, isRowSelected, isLoading, loading: loadingProp = false, loadingLabel = 'Loading records', onExpandedChange, onRowClick, onSortChange, onToggleSort, pagination, renderExpanded, rowActions, rowKey, rowTestId, rows, scrollContainerRef, sort, sortButtonTestId, sortColumn, sortDirection = 'ascending', tableProps, tableRef, testId, ...props }, forwardedRef) {
    const loading = isLoading ?? loadingProp;
    const resolvedRows = React.useMemo(()=>groups ? groups.flatMap((group)=>group.rows) : rows ?? data ?? [], [
        data,
        groups,
        rows
    ]);
    const showRows = !loading && !error;
    const explicitRowId = getRowId ?? rowKey;
    const treeMode = Boolean(getRowChildren || renderExpanded);
    const instanceId = React.useId();
    const [uncontrolledExpanded, setUncontrolledExpanded] = React.useState(()=>defaultExpandedIds ? Array.from(defaultExpandedIds) : []);
    const expandedList = expandedIds ? Array.from(expandedIds) : uncontrolledExpanded;
    const expandedSignature = expandedList.map(String).join('\u0000');
    const toggleExpanded = (id)=>{
        const isExpanded = expandedList.some((key)=>String(key) === String(id));
        const next = isExpanded ? expandedList.filter((key)=>String(key) !== String(id)) : [
            ...expandedList,
            id
        ];
        if (!expandedIds) setUncontrolledExpanded(next);
        onExpandedChange?.(next);
    };
    const missingRowIdentity = !explicitRowId;
    const missingRowActionLabel = Boolean(onRowClick && !getRowActionLabel && !getRowLabel);
    const validFilterRow = filterRow && (filterRow.type === 'tr' || filterRow.type === TableRow) ? filterRow : undefined;
    const invalidFilterRow = Boolean(filterRow && !validFilterRow);
    React.useEffect(()=>{
        if (!developmentDiagnosticsEnabled) return;
        if (missingRowIdentity) {
            console.error('SpecraDataTable requires getRowId (or the deprecated rowKey alias). Render-order fallback ids were applied.');
        }
        if (missingRowActionLabel) {
            console.error('SpecraDataTable requires getRowActionLabel or getRowLabel when onRowClick is provided. The fallback label "Open row" was applied.');
        }
        if (invalidFilterRow) {
            console.error('SpecraDataTable filterRow must be a semantic <tr> or TableRow element. The invalid filter row was omitted.');
        }
    }, [
        invalidFilterRow,
        missingRowActionLabel,
        missingRowIdentity
    ]);
    // The rendered body in order: group header rows, and every visible row of
    // the tree (a child only under an expanded parent).
    const bodyPlan = React.useMemo(()=>{
        const expandedSet = new Set(expandedSignature ? expandedSignature.split('\u0000') : []);
        const items = [];
        const entries = [];
        const walk = (row, depth, parent, groupId)=>{
            const id = explicitRowId?.(row) ?? `__specra_missing_row_${entries.length + 1}`;
            const children = getRowChildren?.(row);
            const hasChildren = Boolean(children?.length);
            const expandable = isRowExpandable ? isRowExpandable(row) : hasChildren || Boolean(renderExpanded);
            const expanded = expandable && expandedSet.has(String(id));
            const entry = {
                depth,
                expandable,
                expanded,
                groupId,
                hasChildren,
                id,
                parent,
                row
            };
            entries.push(entry);
            items.push({
                entry,
                kind: 'row'
            });
            if (expanded && children) for (const child of children)walk(child, depth + 1, row, groupId);
        };
        if (groups) {
            for (const group of groups){
                items.push({
                    group,
                    kind: 'group'
                });
                for (const row of group.rows)walk(row, 0, undefined, group.id);
            }
        } else {
            for (const row of resolvedRows)walk(row, 0, undefined, undefined);
        }
        return {
            entries,
            items
        };
    }, [
        expandedSignature,
        explicitRowId,
        getRowChildren,
        groups,
        isRowExpandable,
        renderExpanded,
        resolvedRows
    ]);
    const rowIdentityPlan = React.useMemo(()=>{
        const semanticIds = bodyPlan.entries.map((entry)=>entry.id);
        return {
            renderPlan: createRenderIdentityPlan(semanticIds.map(String)),
            semanticIds
        };
    }, [
        bodyPlan
    ]);
    const duplicateRowSignature = [
        ...rowIdentityPlan.renderPlan.duplicateIds
    ].sort().join(', ');
    React.useEffect(()=>{
        if (developmentDiagnosticsEnabled && duplicateRowSignature) {
            console.error(`SpecraDataTable row ids must be unique. Duplicate ids: ${duplicateRowSignature}.`);
        }
    }, [
        duplicateRowSignature
    ]);
    const resolvedSortColumn = sort?.key ?? sortColumn;
    const resolvedSortDirection = sort?.dir === 'desc' ? 'descending' : sort?.dir === 'asc' ? 'ascending' : sortDirection;
    const columnIdentityPlan = React.useMemo(()=>createRenderIdentityPlan(columns.map((column, index)=>column.id ?? column.key ?? `column-${index}`)), [
        columns
    ]);
    const rawColumnId = (column, index)=>column.id ?? column.key ?? `column-${index}`;
    const duplicateColumnSignature = [
        ...columnIdentityPlan.duplicateIds
    ].sort().join(', ');
    React.useEffect(()=>{
        if (developmentDiagnosticsEnabled && duplicateColumnSignature) {
            console.error(`SpecraDataTable column ids must be unique. Duplicate ids: ${duplicateColumnSignature}.`);
        }
    }, [
        duplicateColumnSignature
    ]);
    const columnId = rawColumnId;
    const columnKey = (_column, index)=>columnIdentityPlan.identities[index];
    const columnCount = columns.length + (rowActions ? 1 : 0);
    return /*#__PURE__*/ _jsxs("div", {
        ...props,
        "aria-busy": loading ? true : ariaBusy,
        className: className,
        ref: forwardedRef,
        children: [
            /*#__PURE__*/ _jsx("output", {
                className: "sr-only",
                "aria-live": "polite",
                children: loading ? loadingLabel : ''
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: cn(framed && 'rounded-[var(--radius-lg)] border border-border'),
                children: [
                    /*#__PURE__*/ _jsxs(Table, {
                        ...tableProps,
                        className: cn('min-w-[var(--component-data-table-min-width)] text-[length:var(--fs-ui)]', tableProps?.className),
                        containerClassName: cn('max-w-full [scrollbar-width:thin]', tableProps?.containerClassName),
                        containerRef: scrollContainerRef,
                        "data-testid": testId ?? getDataTestId(tableProps),
                        ref: tableRef,
                        children: [
                            caption ? /*#__PURE__*/ _jsx("caption", {
                                className: "sr-only",
                                children: caption
                            }) : null,
                            /*#__PURE__*/ _jsxs(TableHeader, {
                                className: "[&_tr]:border-[var(--border-strong)]",
                                children: [
                                    /*#__PURE__*/ _jsxs(TableRow, {
                                        className: "hover:bg-transparent",
                                        children: [
                                            columns.map((column, columnIndex)=>{
                                                const id = columnId(column, columnIndex);
                                                const activeSort = resolvedSortColumn === id;
                                                const canSort = Boolean(column.sortable && (onSortChange || onToggleSort));
                                                const resolvedSortButtonTestId = typeof sortButtonTestId === 'function' ? sortButtonTestId(id) : sortButtonTestId ? `${sortButtonTestId}-${id}` : undefined;
                                                return /*#__PURE__*/ _jsx(TableHead, {
                                                    "aria-sort": canSort ? activeSort ? resolvedSortDirection : 'none' : undefined,
                                                    className: cn('h-[var(--control-height-md)] px-[var(--space-2)] text-[length:var(--fs-ui-label)] uppercase tracking-[var(--ls-ui-eyebrow)]', column.headerClassName),
                                                    style: {
                                                        textAlign: column.align,
                                                        width: column.width,
                                                        ...column.headerStyle
                                                    },
                                                    children: (()=>{
                                                        const headerNode = canSort ? /*#__PURE__*/ _jsxs("button", {
                                                            "data-col": resolvedSortButtonTestId ? id : undefined,
                                                            "data-testid": resolvedSortButtonTestId,
                                                            className: cn(// Tailwind preflight resets `text-transform` to none on
                                                            // <button>, so a sortable header would drop the <th>'s
                                                            // uppercase and read title-case beside the plain-text
                                                            // headers. Re-assert it here so every header matches.
                                                            '-mx-1 inline-flex min-h-8 items-center gap-1 rounded px-1 text-left uppercase hover:text-foreground', specraFocusRing),
                                                            onClick: ()=>{
                                                                if (onSortChange) {
                                                                    onSortChange(id, activeSort && resolvedSortDirection === 'ascending' ? 'descending' : 'ascending');
                                                                } else {
                                                                    onToggleSort?.(id);
                                                                }
                                                            },
                                                            type: "button",
                                                            children: [
                                                                column.header,
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    "aria-hidden": "true",
                                                                    children: activeSort ? resolvedSortDirection === 'ascending' ? '↑' : '↓' : '↕'
                                                                })
                                                            ]
                                                        }) : column.header;
                                                        // headerAdornment renders as a SIBLING of the sort button (or
                                                        // header text), never inside it - a sortable column already
                                                        // owns the whole header as its click target, so nesting
                                                        // another interactive element inside would both be invalid
                                                        // HTML (button-in-button) and let a click on the adornment
                                                        // bubble into the sort toggle.
                                                        return column.headerAdornment ? /*#__PURE__*/ _jsxs("span", {
                                                            className: "inline-flex items-center gap-1",
                                                            children: [
                                                                headerNode,
                                                                column.headerAdornment
                                                            ]
                                                        }) : headerNode;
                                                    })()
                                                }, columnKey(column, columnIndex));
                                            }),
                                            rowActions ? /*#__PURE__*/ _jsx(TableHead, {
                                                className: "w-px px-[var(--space-2)]",
                                                children: /*#__PURE__*/ _jsx("span", {
                                                    className: "sr-only",
                                                    children: actionsLabel ?? 'Row actions'
                                                })
                                            }) : null
                                        ]
                                    }),
                                    validFilterRow
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(TableBody, {
                                children: [
                                    loading ? /*#__PURE__*/ _jsx(TableRow, {
                                        children: /*#__PURE__*/ _jsx(TableCell, {
                                            className: "h-24 text-center text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                            colSpan: columnCount,
                                            children: /*#__PURE__*/ _jsx("span", {
                                                "aria-hidden": "true",
                                                children: loadingLabel
                                            })
                                        })
                                    }) : null,
                                    showRows ? bodyPlan.items.map((item)=>{
                                        if (item.kind === 'group') {
                                            const { group } = item;
                                            return /*#__PURE__*/ _jsx(TableRow, {
                                                ...group.headerProps,
                                                className: cn('hover:bg-transparent', group.headerProps?.className),
                                                "data-group-key": group.id,
                                                "data-testid": group.testId,
                                                children: /*#__PURE__*/ _jsx("th", {
                                                    className: "px-[var(--space-2)] pb-[var(--space-1-5)] pt-[var(--space-4)] text-left font-normal",
                                                    colSpan: columnCount,
                                                    scope: "colgroup",
                                                    children: /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center gap-[var(--space-3)]",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "min-w-0 font-sans text-[length:var(--fs-ui-eyebrow)] font-medium uppercase leading-[var(--lh-tight)] tracking-[var(--ls-ui-group)] text-muted-foreground",
                                                                children: group.label
                                                            }),
                                                            group.summary ? /*#__PURE__*/ _jsx("span", {
                                                                className: "ml-auto shrink-0 text-[length:var(--fs-ui-meta)] tabular-nums text-muted-foreground",
                                                                children: group.summary
                                                            }) : null
                                                        ]
                                                    })
                                                })
                                            }, `__specra_group_${group.id}`);
                                        }
                                        const { entry } = item;
                                        const { row } = entry;
                                        const rowIndex = bodyPlan.entries.indexOf(entry);
                                        const meta = {
                                            depth: entry.depth,
                                            expandable: entry.expandable,
                                            expanded: entry.expanded,
                                            groupId: entry.groupId,
                                            hasChildren: entry.hasChildren,
                                            parent: entry.parent
                                        };
                                        const resolvedRowProps = getRowProps?.(row);
                                        const { 'aria-selected': rowAriaSelected, className: rowClassName, onClick: rowOnClick, ...nativeRowProps } = resolvedRowProps ?? {};
                                        const rowSelected = isRowSelected?.(row) ?? (rowAriaSelected === true || rowAriaSelected === 'true');
                                        const resolvedRowId = rowIdentityPlan.semanticIds[rowIndex];
                                        const renderIdentity = rowIdentityPlan.renderPlan.identities[rowIndex];
                                        const resolvedRowTestId = typeof rowTestId === 'function' ? rowTestId(row) : rowTestId;
                                        const rowTone = getRowTone?.(row);
                                        const detailId = `${instanceId}-detail-${encodeURIComponent(renderIdentity)}`;
                                        const showDetail = Boolean(renderExpanded && entry.expanded);
                                        const resolvedToggleTestId = typeof expandToggleTestId === 'function' ? expandToggleTestId(row) : expandToggleTestId ? `${expandToggleTestId}-${String(resolvedRowId)}` : undefined;
                                        const toggleLabel = getExpandToggleLabel?.(row, entry.expanded) ?? `${entry.expanded ? 'Collapse' : 'Expand'} ${getRowLabel?.(row) ?? 'row'}`;
                                        const expandedRowProps = showDetail ? getExpandedRowProps?.(row) : undefined;
                                        const tableRow = /*#__PURE__*/ _jsxs(TableRow, {
                                            ...nativeRowProps,
                                            "aria-label": getRowLabel?.(row) ?? nativeRowProps['aria-label'],
                                            "aria-level": getRowChildren ? entry.depth + 1 : undefined,
                                            className: cn(onRowClick && 'cursor-pointer focus-within:outline-none focus-within:ring-2 focus-within:ring-inset focus-within:ring-ring', rowTone && rowToneClass[rowTone], rowClassName),
                                            "data-depth": treeMode ? entry.depth : undefined,
                                            "data-expanded": entry.expandable ? entry.expanded : undefined,
                                            "data-group": entry.groupId,
                                            "data-tone": rowTone,
                                            "data-row-id": resolvedRowId,
                                            "data-selected": rowSelected || undefined,
                                            "data-state": rowSelected ? 'selected' : undefined,
                                            "data-testid": resolvedRowTestId ?? getDataTestId(resolvedRowProps),
                                            onClick: (event)=>{
                                                rowOnClick?.(event);
                                                if (event.defaultPrevented) return;
                                                if (!isInteractiveTarget(event.target)) onRowClick?.(row);
                                            },
                                            children: [
                                                columns.map((column, columnIndex)=>{
                                                    const resolvedCellTestId = typeof column.testId === 'function' ? column.testId(row) : column.testId ? `${column.testId}-${String(resolvedRowId)}` : undefined;
                                                    const content = column.cell?.(row, meta) ?? column.render?.(row, meta);
                                                    return /*#__PURE__*/ _jsxs(TableCell, {
                                                        className: cn('border-[var(--border-subtle)] px-[var(--space-2)] py-[var(--space-2-5)] text-[length:var(--fs-ui)]', // Numbers and dates right-align, and their digits line up.
                                                        column.align === 'right' && 'tabular-nums', column.className),
                                                        "data-testid": resolvedCellTestId,
                                                        style: {
                                                            textAlign: column.align,
                                                            ...column.style
                                                        },
                                                        children: [
                                                            columnIndex === 0 && onRowClick ? /*#__PURE__*/ _jsx("button", {
                                                                "aria-label": getRowActionLabel?.(row) ?? getRowLabel?.(row) ?? 'Open row',
                                                                className: "sr-only",
                                                                "data-row-action": "",
                                                                onClick: (event)=>{
                                                                    event.stopPropagation();
                                                                    onRowClick(row);
                                                                },
                                                                type: "button"
                                                            }) : null,
                                                            columnIndex === 0 && treeMode ? /*#__PURE__*/ _jsxs("div", {
                                                                className: "flex items-center gap-[var(--space-1)]",
                                                                style: entry.depth ? {
                                                                    paddingInlineStart: `calc(var(--component-data-table-indent) * ${entry.depth})`
                                                                } : undefined,
                                                                children: [
                                                                    entry.expandable ? /*#__PURE__*/ _jsx(SpecraIconButton, {
                                                                        ...getExpandToggleProps?.(row),
                                                                        "aria-controls": showDetail ? detailId : undefined,
                                                                        "aria-expanded": entry.expanded,
                                                                        className: "shrink-0 text-muted-foreground",
                                                                        "data-row-toggle": "",
                                                                        "data-testid": resolvedToggleTestId,
                                                                        label: toggleLabel,
                                                                        onClick: (event)=>{
                                                                            event.stopPropagation();
                                                                            toggleExpanded(entry.id);
                                                                        },
                                                                        size: "sm",
                                                                        variant: "ghost",
                                                                        children: /*#__PURE__*/ _jsx(ChevronRight, {
                                                                            "aria-hidden": "true",
                                                                            className: cn('transition-transform', entry.expanded && 'rotate-90')
                                                                        })
                                                                    }) : /*#__PURE__*/ _jsx("span", {
                                                                        "aria-hidden": "true",
                                                                        className: "size-[var(--control-height-sm)] shrink-0"
                                                                    }),
                                                                    /*#__PURE__*/ _jsx("div", {
                                                                        className: "min-w-0 flex-1",
                                                                        children: content
                                                                    })
                                                                ]
                                                            }) : content
                                                        ]
                                                    }, columnKey(column, columnIndex));
                                                }),
                                                rowActions ? /*#__PURE__*/ _jsx(TableCell, {
                                                    className: "w-px px-[var(--space-2)] py-[var(--space-2-5)] text-right",
                                                    onClick: (event)=>event.stopPropagation(),
                                                    children: rowActions(row)
                                                }) : null
                                            ]
                                        });
                                        // One keyed fragment whether or not the detail row shows, so
                                        // opening it never remounts the row (and drops the toggle's focus).
                                        return /*#__PURE__*/ _jsxs(React.Fragment, {
                                            children: [
                                                tableRow,
                                                showDetail ? /*#__PURE__*/ _jsx(TableRow, {
                                                    ...expandedRowProps,
                                                    className: cn('hover:bg-transparent', expandedRowProps?.className),
                                                    "data-expanded-row": "",
                                                    id: detailId,
                                                    children: /*#__PURE__*/ _jsx(TableCell, {
                                                        className: "bg-muted/30 px-[var(--space-3)] py-[var(--space-2)]",
                                                        colSpan: columnCount,
                                                        children: renderExpanded?.(row)
                                                    })
                                                }) : null
                                            ]
                                        }, renderIdentity);
                                    }) : null
                                ]
                            }),
                            footerRow && showRows && bodyPlan.entries.length > 0 ? /*#__PURE__*/ _jsx(TableFooter, {
                                className: "border-border-strong",
                                children: /*#__PURE__*/ _jsxs(TableRow, {
                                    className: "hover:bg-transparent",
                                    children: [
                                        columns.map((column, columnIndex)=>/*#__PURE__*/ _jsx(TableCell, {
                                                className: cn('px-[var(--space-2)] py-[var(--space-2-5)] text-[length:var(--fs-ui)]', column.align === 'right' && 'tabular-nums'),
                                                style: {
                                                    textAlign: column.align
                                                },
                                                children: footerRow[columnId(column, columnIndex)]
                                            }, columnKey(column, columnIndex))),
                                        rowActions ? /*#__PURE__*/ _jsx(TableCell, {
                                            className: "w-px"
                                        }) : null
                                    ]
                                })
                            }) : null
                        ]
                    }),
                    !loading && error ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        className: "m-[var(--space-2)]",
                        tone: "danger",
                        variant: "framed",
                        children: error
                    }) : null,
                    showRows && resolvedRows.length === 0 ? empty ?? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        action: emptyAction,
                        description: emptyDescription,
                        title: emptyTitle
                    }) : null
                ]
            }),
            pagination && !loading ? /*#__PURE__*/ _jsx(SpecraPagination, {
                ...pagination,
                className: cn('mt-[var(--space-3)]', pagination.className)
            }) : null
        ]
    });
}
const SpecraDataTableRoot = /*#__PURE__*/ React.forwardRef(SpecraDataTableInner);
SpecraDataTableRoot.displayName = 'SpecraDataTable';
export const SpecraDataTable = SpecraDataTableRoot;
